import { useState, useEffect, useCallback } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Modal, Button, Form, Pagination } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import { toast } from 'react-toastify';
import './SongContributionsList.css';
import { extractObjectId, formatDate, formatDateTime } from '../../../utils/utils';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { FaMusic, FaCheck, FaTimes, FaEye, FaTrash, FaYoutube, FaFileAlt } from 'react-icons/fa';

const SongContributionsList = () => {
    const [contributions, setContributions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedContribution, setSelectedContribution] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const itemsPerPage = 10;

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkRejectionReason, setBulkRejectionReason] = useState('');
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkAction, setBulkAction] = useState('');

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);

    const fetchContributions = useCallback(async (page = 1, status = '') => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: itemsPerPage,
            };
            if (status) {
                params.status = status;
            }
            const response = await allAPiServicesCall.getAllSongContributions(params, getAuthConfig());
            if (response?.status === 200) {
                setContributions(response.data || []);
                if (response.pagination) {
                    setCurrentPage(response.pagination.currentPage);
                    setTotalPages(response.pagination.totalPages);
                    setTotalCount(response.pagination.totalCount);
                }
            } else {
                setContributions([]);
            }
        } catch (error) {
            console.error("Error fetching contributions:", error);
            toast.error('Error fetching contributions');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchContributionDetails = async (id) => {
        const contributionId = extractObjectId(id);
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getSongContributionById(getAuthConfig(), contributionId);
            if (response?.data) {
                setSelectedContribution(response.data);
                setModalOpen(true);
            }
        } catch (error) {
            toast.error('Error fetching contribution details');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveOrReject = async (id, status) => {
        if (status === 'rejected' && !rejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionId: extractObjectId(id),
                status: status,
                ...(status === 'rejected' && { rejectionReason: rejectionReason.trim() }),
            };
            await allAPiServicesCall.processSongContribution(payload, getAuthConfig());

            setModalOpen(false);
            setRejectionReason('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setProcessing(false);
        }
    };

    const openDeleteModal = (id) => {
        setDeleteTargetId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!deleteTargetId) return;

        setProcessing(true);
        try {
            const contributionId = extractObjectId(deleteTargetId);
            await allAPiServicesCall.deleteSongContribution({ id: contributionId }, getAuthConfig());
            setShowDeleteModal(false);
            setDeleteTargetId(null);
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error('Error deleting contribution');
        } finally {
            setProcessing(false);
        }
    };

    // Bulk selection handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pendingIds = contributions
                .filter(c => c.status === 'pending')
                .map(c => extractObjectId(c._id));
            setSelectedIds(pendingIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        const extractedId = extractObjectId(id);
        setSelectedIds(prev => {
            if (prev.includes(extractedId)) {
                return prev.filter(i => i !== extractedId);
            } else {
                return [...prev, extractedId];
            }
        });
    };

    const handleBulkAction = async () => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one contribution');
            return;
        }

        if (bulkAction === 'rejected' && !bulkRejectionReason.trim()) {
            toast.warning('Please provide a reason for rejection');
            return;
        }

        setProcessing(true);
        try {
            const payload = {
                contributionIds: selectedIds,
                status: bulkAction,
                ...(bulkAction === 'rejected' && { rejectionReason: bulkRejectionReason.trim() }),
            };
            await allAPiServicesCall.bulkProcessSongContributions(payload, getAuthConfig());

            setShowBulkModal(false);
            setBulkRejectionReason('');
            setSelectedIds([]);
            setBulkAction('');
            fetchContributions(currentPage, statusFilter);
        } catch (error) {
            toast.error(error.message || 'Error processing bulk action');
        } finally {
            setProcessing(false);
        }
    };

    const openBulkModal = (action) => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one pending contribution');
            return;
        }
        setBulkAction(action);
        setShowBulkModal(true);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            pending: { backgroundColor: '#ffc107', color: '#000' },
            approved: { backgroundColor: '#28a745', color: '#fff' },
            rejected: { backgroundColor: '#dc3545', color: '#fff' },
        };
        return (
            <span
                className="badge px-2 py-1 rounded"
                style={statusStyles[status] || statusStyles.pending}
            >
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedIds([]);
        fetchContributions(page, statusFilter);
    };

    const handleStatusFilterChange = (e) => {
        const newStatus = e.target.value;
        setStatusFilter(newStatus);
        setCurrentPage(1);
        setSelectedIds([]);
        fetchContributions(1, newStatus);
    };

    // Extract YouTube video ID from URL
    const extractYouTubeVideoId = (url) => {
        if (!url) return null;
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
            /^([a-zA-Z0-9_-]{11})$/,
        ];
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) return match[1];
        }
        return null;
    };

    // Generate pagination items
    const renderPagination = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={currentPage === 1} />,
            <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
        );

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />,
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        );

        return items;
    };

    useEffect(() => {
        fetchContributions(1, '');
    }, [fetchContributions]);

    const pendingCount = contributions.filter(c => c.status === 'pending').length;

    return (
        <CommonLayout>
            <div className="song-contributions-container">
                {/* Premium Header */}
                <div className="sc-header">
                    <div className="sc-header-top">
                        <div className="sc-title-wrapper">
                            <div className="sc-title-icon">
                                <FaMusic />
                            </div>
                            <div className="sc-title-text">
                                <h1 className="sc-title">Song Contributions</h1>
                                <p className="sc-subtitle">Review and approve songs submitted by users</p>
                            </div>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat-badge">
                                <FaMusic />
                                <span>Total: <strong>{totalCount}</strong></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter and Bulk Actions Bar */}
                <div className="sc-filter-section">
                    <div className="sc-filter-left">
                        <select
                            className="sc-filter-select"
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <div className="sc-filter-info">
                            <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                        </div>
                    </div>

                    {/* Bulk Action Buttons */}
                    <div className="sc-filter-right">
                        <button
                            className="sc-bulk-btn sc-bulk-btn-approve"
                            onClick={() => openBulkModal('approved')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaCheck /> Bulk Approve ({selectedIds.length})
                        </button>
                        <button
                            className="sc-bulk-btn sc-bulk-btn-reject"
                            onClick={() => openBulkModal('rejected')}
                            disabled={selectedIds.length === 0}
                        >
                            <FaTimes /> Bulk Reject ({selectedIds.length})
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="sc-table-card">
                        <div className="sc-loading">
                            <div className="sc-loading-spinner"></div>
                            <p className="sc-loading-text">Loading contributions...</p>
                        </div>
                    </div>
                ) : contributions.length > 0 ? (
                    <div className="sc-table-card">
                        <div className="sc-table-container">
                            <table className="sc-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                className="sc-checkbox"
                                                onChange={handleSelectAll}
                                                checked={pendingCount > 0 && selectedIds.length === pendingCount}
                                                disabled={pendingCount === 0}
                                                title="Select all pending contributions"
                                            />
                                        </th>
                                        <th>Song</th>
                                        <th>Artist</th>
                                        <th>Album</th>
                                        <th>YouTube</th>
                                        <th>Has Lyrics</th>
                                        <th>Contributed By</th>
                                        <th>Status</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contributions.map((contribution) => (
                                        <tr key={contribution._id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="sc-checkbox"
                                                    checked={selectedIds.includes(extractObjectId(contribution._id))}
                                                    onChange={() => handleSelectOne(contribution._id)}
                                                    disabled={contribution.status !== 'pending'}
                                                />
                                            </td>
                                            <td><span className="sc-song-name">{contribution.songName || 'Unknown Song'}</span></td>
                                            <td><span className="sc-artist-name">{contribution.artistName || 'Unknown Artist'}</span></td>
                                            <td>
                                                {contribution.albumName ? (
                                                    <span className="sc-album-name">{contribution.albumName}</span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {contribution.youtubeUrl ? (
                                                    <a
                                                        href={contribution.youtubeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="sc-youtube-link"
                                                    >
                                                        <FaYoutube /> Watch
                                                    </a>
                                                ) : contribution.hasNoOfficialVideo ? (
                                                    <span className="sc-no-video-badge">No Video</span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`sc-lyrics-badge ${contribution.lyrics ? 'yes' : 'no'}`}>
                                                    {contribution.lyrics ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td><span className="sc-contributor">{contribution.user_data?.username || 'N/A'}</span></td>
                                            <td>
                                                <span className={`sc-status-badge ${contribution.status}`}>
                                                    {contribution.status?.charAt(0).toUpperCase() + contribution.status?.slice(1)}
                                                </span>
                                            </td>
                                            <td><span className="sc-date">{formatDate(contribution.createdAt)}</span></td>
                                            <td>
                                                <div className="sc-actions">
                                                    <button
                                                        className="sc-action-btn sc-action-btn-view"
                                                        onClick={() => fetchContributionDetails(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaEye /> View
                                                    </button>
                                                    <button
                                                        className="sc-action-btn sc-action-btn-delete"
                                                        onClick={() => openDeleteModal(contribution._id)}
                                                        disabled={loading}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="sc-pagination-wrapper">
                                <Pagination>{renderPagination()}</Pagination>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="sc-table-card">
                        <div className="sc-empty">
                            <div className="sc-empty-icon">
                                <FaMusic />
                            </div>
                            <p className="sc-empty-text">No song contributions found.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {modalOpen && selectedContribution && (
                <Modal show={modalOpen} onHide={() => { setModalOpen(false); setRejectionReason(''); }} size="lg" centered className="contribution-detail-modal">
                    <Modal.Header className="contribution-detail-header">
                        <div className="contribution-detail-header-content">
                            <div className="contribution-detail-icon">
                                <FaMusic size={28} />
                            </div>
                            <div>
                                <Modal.Title className="contribution-detail-title">
                                    {selectedContribution.songName || 'Song Contribution'}
                                </Modal.Title>
                                <p className="contribution-detail-subtitle">
                                    by {selectedContribution.artistName || 'Unknown Artist'}
                                    {selectedContribution.albumName && ` - ${selectedContribution.albumName}`}
                                </p>
                            </div>
                        </div>
                        <button className="contribution-detail-close" onClick={() => { setModalOpen(false); setRejectionReason(''); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </Modal.Header>
                    <Modal.Body className="contribution-detail-body">
                        {/* Info Cards */}
                        <div className="contribution-info-grid">
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Contributed By</span>
                                <span className="contribution-info-value">{selectedContribution.user_data?.username || 'Unknown'}</span>
                                <span className="contribution-info-email">{selectedContribution.user_data?.email || ''}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Status</span>
                                <span className="contribution-info-value">{getStatusBadge(selectedContribution.status)}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Submitted</span>
                                <span className="contribution-info-value">{formatDate(selectedContribution.createdAt)}</span>
                                <span className="contribution-info-email">{formatDateTime(selectedContribution.createdAt, '').split(', ')[1] || ''}</span>
                            </div>
                        </div>

                        <div className="contribution-info-grid">
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Track Number</span>
                                <span className="contribution-info-value">{selectedContribution.trackNumber || 'Not specified'}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Release Year</span>
                                <span className="contribution-info-value">{selectedContribution.releaseYear || 'Not specified'}</span>
                            </div>
                            <div className="contribution-info-card">
                                <span className="contribution-info-label">Genre</span>
                                <span className="contribution-info-value">{selectedContribution.genre || 'Not specified'}</span>
                            </div>
                        </div>

                        {/* YouTube Video Preview */}
                        {selectedContribution.youtubeUrl && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon current">
                                        <FaYoutube />
                                    </span>
                                    <span className="contribution-section-title">YouTube Video</span>
                                </div>
                                <div className="sc-youtube-embed">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${extractYouTubeVideoId(selectedContribution.youtubeUrl)}`}
                                        title="YouTube video player"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            </div>
                        )}

                        {selectedContribution.hasNoOfficialVideo && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon rejected">
                                        <FaYoutube />
                                    </span>
                                    <span className="contribution-section-title">No Official Video</span>
                                </div>
                                <div className="contribution-lyrics-box rejected">
                                    The contributor indicated that this song has no official YouTube video available.
                                </div>
                            </div>
                        )}

                        {/* Featured Artists */}
                        {selectedContribution.featuredArtists?.length > 0 && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon meaning">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Featured Artists</span>
                                </div>
                                <div className="contribution-lyrics-box meaning">
                                    {selectedContribution.featuredArtists.join(', ')}
                                </div>
                            </div>
                        )}

                        {/* Lyrics */}
                        {selectedContribution.lyrics && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon new">
                                        <FaFileAlt />
                                    </span>
                                    <span className="contribution-section-title">Lyrics</span>
                                    <span className="contribution-section-badge new">+5 Bonus</span>
                                </div>
                                <div className="contribution-lyrics-box new">
                                    {selectedContribution.lyrics}
                                </div>
                            </div>
                        )}

                        {/* Song Meaning */}
                        {selectedContribution.songMeaning && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon meaning">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Song Meaning / Description</span>
                                </div>
                                <div className="contribution-lyrics-box meaning">
                                    {selectedContribution.songMeaning}
                                </div>
                            </div>
                        )}

                        {/* Credits */}
                        {selectedContribution.credits?.length > 0 && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon current">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="8.5" cy="7" r="4"></circle>
                                            <line x1="20" y1="8" x2="20" y2="14"></line>
                                            <line x1="23" y1="11" x2="17" y2="11"></line>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Credits</span>
                                </div>
                                <div className="contribution-lyrics-box current">
                                    {selectedContribution.credits.map((credit, index) => (
                                        <div key={index} style={{ marginBottom: '0.5rem' }}>
                                            <strong>{credit.title}:</strong> {credit.description}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Rejection Reason (if rejected) */}
                        {selectedContribution.status === 'rejected' && selectedContribution.rejectionReason && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon rejected">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="15" y1="9" x2="9" y2="15"></line>
                                            <line x1="9" y1="9" x2="15" y2="15"></line>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Rejection Reason</span>
                                </div>
                                <div className="contribution-lyrics-box rejected">
                                    {selectedContribution.rejectionReason}
                                </div>
                            </div>
                        )}

                        {/* Rejection Input (if pending) */}
                        {selectedContribution.status === 'pending' && (
                            <div className="contribution-section">
                                <div className="contribution-section-header">
                                    <span className="contribution-section-icon rejected">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                    </span>
                                    <span className="contribution-section-title">Rejection Reason (if rejecting)</span>
                                </div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter reason for rejection..."
                                    className="contribution-rejection-input"
                                />
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="contribution-detail-footer">
                        {selectedContribution.status === 'pending' && (
                            <>
                                <Button
                                    className="contribution-action-btn approve"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'approved')}
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck className="me-2" />
                                            Approve & Create Song
                                        </>
                                    )}
                                </Button>
                                <Button
                                    className="contribution-action-btn reject"
                                    onClick={() => handleApproveOrReject(selectedContribution._id, 'rejected')}
                                    disabled={processing}
                                >
                                    {processing ? 'Processing...' : (
                                        <>
                                            <FaTimes className="me-2" />
                                            Reject
                                        </>
                                    )}
                                </Button>
                            </>
                        )}
                        <Button
                            className="contribution-action-btn close-btn"
                            onClick={() => { setModalOpen(false); setRejectionReason(''); }}
                            disabled={processing}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Bulk Action Modal */}
            <ConfirmationModal
                show={showBulkModal}
                handleClose={() => { setShowBulkModal(false); setBulkRejectionReason(''); }}
                onConfirm={handleBulkAction}
                title={`Bulk ${bulkAction === 'approved' ? 'Approve' : 'Reject'} Song Contributions`}
                message={`You are about to ${bulkAction === 'approved' ? 'approve' : 'reject'} ${selectedIds.length} song contribution(s).${bulkAction === 'approved' ? ' New songs will be created immediately.' : ''}`}
                confirmText={`Confirm ${bulkAction === 'approved' ? 'Approve' : 'Reject'}`}
                cancelText="Cancel"
                variant={bulkAction === 'approved' ? 'success' : 'danger'}
                isLoading={processing}
                confirmDisabled={bulkAction === 'rejected' && !bulkRejectionReason.trim()}
            >
                {bulkAction === 'rejected' && (
                    <Form.Group>
                        <Form.Label><strong>Rejection Reason (required):</strong></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={bulkRejectionReason}
                            onChange={(e) => setBulkRejectionReason(e.target.value)}
                            placeholder="Enter reason for bulk rejection..."
                        />
                    </Form.Group>
                )}
            </ConfirmationModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
                onConfirm={handleDelete}
                title="Delete Song Contribution"
                message="Are you sure you want to delete this song contribution? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={processing}
            />
        </CommonLayout>
    );
};

export default SongContributionsList;
